import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { ChevronLeft, ChevronRight, History, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { DOCUMENT_CONTENT_QUERY, DOCUMENTS_QUERY } from '../graphql/queries';
import { DELETE_DOCUMENT_MUTATION } from '../graphql/mutations';
import { Markdown } from '../components/Markdown';
import { DocumentEditor } from '../components/DocumentEditor';
import { useWikilinkResolver } from '../hooks/useWikilinkResolver';
import { useSwipeNav } from '../hooks/useSwipeNav';
import { DocumentVersionsDialog } from '../components/DocumentVersionsDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DocTreeAside, DocTreeNav } from '../components/DocTreeNav';
import { FetchErrorCard } from '../components/FetchErrorCard';
import { DocumentOutboxBanner } from '../components/OutboxBanner';
import { JournalDocView, ListDocView, NoteDocView } from '../components/DocumentViews';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import {
  adjacentJournalDays,
  displayBody,
  documentTitle,
  fileStem,
  friendlyDay,
  journalDayFromPath,
  docViewType,
  parseJournalView,
  parseListView,
  parseNoteView,
  type JournalView,
  type ListView,
  type NoteView,
} from '../lib/docView';

interface DocumentContentData {
  documentContent: {
    path: string;
    content: string;
    updatedAt: string | null;
    version: number;
  } | null;
}

interface DocumentsData {
  documents: { path: string }[];
}

/** The per-type rendered view a document's content parses into, if any. */
type DocView =
  | { kind: 'list'; view: ListView }
  | { kind: 'journal'; view: JournalView }
  | { kind: 'note'; view: NoteView }
  | { kind: 'generic' };

/**
 * Picks the rendered view for a document: branch on the display type
 * (front-matter `type`, else the conventional folder), and fall back to the
 * generic Markdown render whenever the tolerant parse doesn't recognize the
 * type's shape. Display-only — never validates, never rejects. `title` is the
 * page h1; a body-opening heading repeating it is dropped from the view.
 */
function resolveDocView(path: string, content: string, title: string): DocView {
  const type = docViewType(path, content);
  if (type === 'list') {
    const view = parseListView(content, [title, fileStem(path)]);
    if (view) return { kind: 'list', view };
  } else if (type === 'journal') {
    const view = parseJournalView(path, content);
    if (view) return { kind: 'journal', view };
  } else if (type === 'note') {
    const view = parseNoteView(path, content);
    if (view) return { kind: 'note', view };
  }
  return { kind: 'generic' };
}

/**
 * The "⋯" overflow menu on the document page. Destructive actions live here
 * rather than as naked header icons (design review D2): one quiet trigger, a
 * small menu, Escape/outside-click to dismiss, focus handed back on close.
 */
function DocumentActionsMenu({ onDelete }: { onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) itemRef.current?.focus();
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        className={`min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors ${FOCUS_RING_CLASSES}`}
      >
        <MoreHorizontal className="w-5 h-5" aria-hidden />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={close} />
          <div
            role="menu"
            aria-label="Document actions"
            className="absolute right-0 top-full mt-1 z-50 min-w-[12rem] glass-panel bg-[var(--surface)] border border-[var(--card-border)] rounded-xl shadow-warm p-1"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                close();
              }
            }}
          >
            <button
              ref={itemRef}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-[var(--danger)] hover:bg-[color-mix(in_oklab,var(--danger)_8%,transparent)] transition-colors ${FOCUS_RING_CLASSES}`}
            >
              <Trash2 className="w-4 h-4" aria-hidden /> Delete document
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** One quiet chevron link to an adjacent journal day. */
function DayNavLink({
  to,
  day,
  direction,
}: {
  to: string;
  day: string;
  direction: 'previous' | 'next';
}) {
  const label = friendlyDay(day, 'short');
  return (
    <Link
      to={`/docs/${to}`}
      aria-label={`${direction === 'previous' ? 'Previous day' : 'Next day'}: ${friendlyDay(day, 'long')}`}
      className={`inline-flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium text-[var(--text-tertiary)] hover:text-[var(--accent-ink)] transition-colors ${FOCUS_RING_CLASSES}`}
    >
      {direction === 'previous' && <ChevronLeft className="w-4 h-4" aria-hidden />}
      {label}
      {direction === 'next' && <ChevronRight className="w-4 h-4" aria-hidden />}
    </Link>
  );
}

/**
 * Prev/next links between a journal day and its nearest EXISTING neighbors in
 * the same tree — the visible counterpart of the swipe gesture, and the way
 * keyboard and desktop users flip through days.
 */
function JournalDayNav({ previous, next }: { previous: string | null; next: string | null }) {
  const previousDay = previous ? journalDayFromPath(previous) : null;
  const nextDay = next ? journalDayFromPath(next) : null;
  if (!previousDay && !nextDay) return null;
  return (
    <nav aria-label="Journal days" className="flex items-center justify-between px-1">
      {previousDay && previous ? (
        <DayNavLink to={previous} day={previousDay} direction="previous" />
      ) : (
        <span aria-hidden />
      )}
      {nextDay && next && <DayNavLink to={next} day={nextDay} direction="next" />}
    </nav>
  );
}

/** Rendered document page: per-type view, inline editor, history, delete. */
export function DocumentPage() {
  const resolveWikilink = useWikilinkResolver();
  const params = useParams();
  const navigate = useNavigate();
  const path = params['*'] ?? '';
  const [editing, setEditing] = useState(false);
  // The offline-conflict review draft: set when the user chooses "Review your
  // version" on the outbox banner — the editor opens with the queued offline
  // text as the draft over the LATEST server text (and version) as the base.
  // The queued entry survives until this review is saved or discarded, so a
  // stray Cancel never loses the offline edit.
  const [reviewDraft, setReviewDraft] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [restoredNote, setRestoredNote] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error, refetch } = useQuery<DocumentContentData>(DOCUMENT_CONTENT_QUERY, {
    variables: { path },
    fetchPolicy: 'cache-and-network',
  });

  // The document tree, for journal-day neighbor lookups. Cache-first and
  // error-ignoring: the app usually already holds it, and without it the day
  // simply has no prev/next affordance — never a degraded page.
  const { data: docsData } = useQuery<DocumentsData>(DOCUMENTS_QUERY, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  // Nearest existing journal days around this one (same tree); both null for
  // non-journal documents, which keeps every affordance below journal-only.
  const { previous: previousDay, next: nextDay } = useMemo(
    () =>
      adjacentJournalDays(
        path,
        (docsData?.documents ?? []).map((doc) => doc.path)
      ),
    [path, docsData?.documents]
  );

  // Swipe left = forward in time (next day), swipe right = back — calendar
  // paging. Disabled while editing or while a dialog or the tree drawer holds
  // the screen, so a stray drag never throws away context mid-task.
  const swipeEnabled = !editing && !versionsOpen && !confirmDeleteOpen && !treeOpen;
  const swipeHandlers = useSwipeNav(
    swipeEnabled && nextDay ? () => navigate(`/docs/${nextDay}`) : null,
    swipeEnabled && previousDay ? () => navigate(`/docs/${previousDay}`) : null
  );

  const [deleteDocument, { loading: deleting }] = useMutation(DELETE_DOCUMENT_MUTATION, {
    refetchQueries: ['Documents'],
  });

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    []
  );

  const doc = data?.documentContent ?? null;
  // Human display title (design review B1): friendly day for journal files,
  // front-matter title when present, else the humanized filename stem. The
  // raw path stays visible as quiet metadata below.
  const title = documentTitle(path, doc?.content ?? null);

  const view = useMemo<DocView>(() => {
    if (!doc || doc.content.trim().length === 0) return { kind: 'generic' };
    return resolveDocView(path, doc.content, title);
  }, [doc, path, title]);

  const handleRestored = (restoredToTime: string) => {
    setVersionsOpen(false);
    setRestoredNote(`Restored to how it was ${restoredToTime}.`);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setRestoredNote(null), 4000);
  };

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    setDeleteError(false);
    try {
      await deleteDocument({ variables: { path } });
      navigate('/docs');
    } catch {
      setDeleteError(true);
    }
  };

  return (
    <div className="xl:flex xl:items-start xl:gap-8">
      {/* Wide desktop: the tree docks beside the document. Below xl the
          overlay drawer (toggle in the actions row) takes over. */}
      <DocTreeAside activePath={path} />

      <div className="max-w-3xl mx-auto xl:mx-0 xl:flex-1 xl:min-w-0 space-y-6" {...swipeHandlers}>
        <div className="space-y-2 px-1 pt-4 sm:pt-6 pb-2">
          {/* The title gets the full row width — the actions live on their own
            row below, so a long name never squeezes into mid-word breaks
            (design review D1/D2). */}
          <h1 className="text-2xl sm:text-3xl font-display font-semibold text-[var(--text-primary)] break-words [text-wrap:balance]">
            {title}
          </h1>
          <p className="text-xs font-mono text-[var(--text-tertiary)] truncate" title={path}>
            {path}
          </p>
          <div role="status" aria-live="polite">
            {restoredNote && (
              <p className="text-sm text-[var(--accent-ink)]" data-testid="restored-note">
                {restoredNote}
              </p>
            )}
          </div>
          {doc && !editing && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <DocTreeNav
                activePath={path}
                buttonClassName="xl:hidden"
                onOpenChange={setTreeOpen}
              />
              <button
                type="button"
                onClick={() => setVersionsOpen(true)}
                className="btn btn-ghost text-sm gap-2"
              >
                <History className="w-4 h-4" aria-hidden /> History
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="btn btn-primary text-sm gap-2"
              >
                <Pencil className="w-4 h-4" aria-hidden /> Edit
              </button>
              <DocumentActionsMenu onDelete={() => setConfirmDeleteOpen(true)} />
            </div>
          )}
        </div>

        {!editing && <JournalDayNav previous={previousDay} next={nextDay} />}

        {!editing && (
          <DocumentOutboxBanner
            path={path}
            onReview={(queuedContent) => {
              setReviewDraft(queuedContent);
              setEditing(true);
            }}
          />
        )}

        {deleteError && (
          <div
            role="alert"
            className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm"
          >
            This wasn't deleted. Something went wrong on our end — try again.
          </div>
        )}

        {editing ? (
          <DocumentEditor
            path={path}
            initialContent={doc?.content ?? ''}
            version={doc?.version ?? null}
            initialDraft={reviewDraft ?? undefined}
            onCancel={() => {
              setEditing(false);
              setReviewDraft(null);
            }}
            onSaved={() => {
              // The editor already resolved the queued entry: a landed save
              // dropped it, an offline save re-queued it as pending.
              setEditing(false);
              setReviewDraft(null);
            }}
            onConflictReload={refetch}
          />
        ) : error && !doc ? (
          <FetchErrorCard>
            Something went wrong loading this document. Try again in a moment.
          </FetchErrorCard>
        ) : loading && !data ? (
          <div className="space-y-2.5" aria-hidden="true">
            <span className="skeleton block h-4 w-3/4 rounded-md" />
            <span className="skeleton block h-4 w-full rounded-md" />
            <span className="skeleton block h-4 w-5/6 rounded-md" />
          </div>
        ) : !doc ? (
          <section className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft">
            <p className="text-[var(--text-secondary)]">
              This document doesn't exist yet. You can create it from the Documents page.
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="btn btn-primary text-sm gap-2"
              >
                <Pencil className="w-4 h-4" aria-hidden /> Write it
              </button>
            </div>
          </section>
        ) : doc.content.trim().length === 0 ? (
          <section className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft">
            <p className="text-[var(--text-secondary)]">This document is empty.</p>
          </section>
        ) : view.kind === 'list' ? (
          <ListDocView
            key={path}
            path={path}
            content={doc.content}
            version={doc.version}
            view={view.view}
            onStaleReload={refetch}
          />
        ) : view.kind === 'journal' ? (
          <JournalDocView view={view.view} />
        ) : view.kind === 'note' ? (
          <NoteDocView view={view.view} updatedAt={doc.updatedAt} />
        ) : (
          <article className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up">
            <Markdown
              content={displayBody(doc.content, title)}
              className="font-reading text-sm sm:text-base"
              resolveWikilink={resolveWikilink}
            />
          </article>
        )}

        {versionsOpen && (
          <DocumentVersionsDialog
            path={path}
            onClose={() => setVersionsOpen(false)}
            onRestored={handleRestored}
          />
        )}

        <ConfirmDialog
          open={confirmDeleteOpen}
          title="Delete this document?"
          body={`"${title}" will be removed from your documents. Recent versions stay in the recovery buffer for a while, but there is no undelete button.`}
          confirmLabel="Delete document"
          cancelLabel="Keep it"
          tone="destructive"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
        {deleting && (
          <span className="sr-only" role="status" aria-live="polite">
            Deleting…
          </span>
        )}
      </div>
    </div>
  );
}
